import { MagnifyingGlassIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import { useLanguage } from "contexts/LanguageContext";
import { useQuotations } from "hooks/useQuotations"; 
import { useEffect, useState } from "react";
import { Customer } from "types/customer";
import { Quotation, QuotationStatus } from "types/quotation";

interface QuotationSelectPageProps {
  selectedCustomer: Customer;
  setSelectedQuotation: (quotation: Quotation | null) => void;
  handleNextStep: () => void;
  handleSkipStep: () => void;
}

// Státusz alapján szín meghatározása
const getStatusColor = (status: QuotationStatus): string => {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-700';
    case 'editing':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-amber-100 text-amber-700';
    case 'rejected':
      return 'bg-red-100 text-red-700';
    case 'success':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-purple-100 text-purple-700';
  }
};

const QuotationSelectPage = ({
  selectedCustomer,
  setSelectedQuotation,
  handleNextStep,
  handleSkipStep,
}: QuotationSelectPageProps) => {
  const { t } = useLanguage();
  const { quotations, loading: quotationsLoading } = useQuotations();
  const [searchQuery, setSearchQuery] = useState('');
  const [customerQuotations, setCustomerQuotations] = useState<Quotation[]>([]);
  
  // Az ügyfélhez tartozó árajánlatok betöltése
  useEffect(() => {
    if (selectedCustomer && quotations.length > 0) {
      const filteredQuotations = quotations.filter(
        quotation => quotation.customerId === selectedCustomer.id
      );
      setCustomerQuotations(filteredQuotations);
    }
  }, [selectedCustomer, quotations]);
  
  // Szűrt árajánlatok a keresés alapján
  const filteredQuotations = customerQuotations.filter(
    quotation => quotation.number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-[650px]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-1">
            {t('documents.selectBaseQuotation')}
          </h3>
          <p className="text-xs text-gray-500">
            {t('documents.selectBaseQuotationDescription')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 flex-1 flex flex-col shadow-sm">
        {/* Keresés */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t('documents.searchQuotation')}
              className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 pl-10 text-sm text-gray-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Árajánlatok listája */}
        <div className="flex-1 h-[450px] overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-300/70">
          {quotationsLoading ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner size="md" color="teal-500" />
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 mb-1">
                {searchQuery ? t('documents.noResults') : t('documents.noQuotationsForCustomer')}
              </p>
              <button
                onClick={handleSkipStep}
                className="mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm"
              >
                {t('documents.skipQuotationSelection')}
              </button>
            </div>
          ) : (
            <div className="space-y-2 pb-2">
              {filteredQuotations.map((quotation) => (
                <div
                  key={quotation.id}
                  onClick={() => {
                    setSelectedQuotation(quotation);
                    handleNextStep();
                  }}
                  className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 cursor-pointer transition-all shadow-sm"
                >
                  <div className="p-2 rounded bg-gradient-to-br from-purple-100 to-purple-200 mr-3">
                    <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {quotation.number}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('documents.createdAt', { date: quotation.createdAt.toLocaleDateString('hu-HU') })}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quotation.status)}`}>
                      {t(`documents.statuses.${quotation.status}`)}
                    </span>
                  </div>
                </div>
              ))}
              
              {/* "Átugrás" gomb a lista alján */}
              <div className="pt-4 text-center">
                <button
                  onClick={handleSkipStep}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm"
                >
                  {t('documents.skipQuotationSelection')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationSelectPage; 